/**
 * Onboarding API Service
 *
 * API functions for client onboarding progress tracking.
 * Used by adaptive dashboard to show onboarding checklist vs mature client view.
 *
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// Re-export response types for consumers
export type { TimComboAPIResponse };

// =============================================================================
// TYPES
// =============================================================================

/**
 * Individual onboarding step
 */
export interface OnboardingStep {
  step_id: string;
  step_name: string;
  step_description: string;
  step_order: number;
  is_completed: boolean;
  is_required: boolean;
  completed_at: string | null;
  action_type: 'link' | 'modal' | 'api_call';
  action_target: string;
  icon?: string;
}

/**
 * Onboarding progress response
 */
export interface OnboardingProgress {
  Client_ID: string;
  Client_Name: string;
  completion_percentage: number;
  total_steps: number;
  completed_steps: number;
  required_steps_completed: number;
  total_required_steps: number;
  steps: OnboardingStep[];
  started_at: string;
  last_activity_at: string | null;
  estimated_time_remaining_minutes?: number;
}

/**
 * Update onboarding step request
 */
export interface UpdateOnboardingStepRequest {
  step_id: string;
  is_completed: boolean;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get onboarding progress for a client
 * GET /api/onboarding/progress
 */
export async function getOnboardingProgress(
  clientId: string
): Promise<TimComboAPIResponse<OnboardingProgress>> {
  return Make_Authorized_API_Call<OnboardingProgress>(
    API_ENDPOINTS.ONBOARDING.PROGRESS,
    'GET',
    undefined,
    { params: { Client_ID: clientId } }
  );
}

/**
 * Update an onboarding step's completion status
 * PUT /api/onboarding/step
 */
export async function updateOnboardingStep(
  clientId: string,
  data: UpdateOnboardingStepRequest
): Promise<TimComboAPIResponse<OnboardingStep>> {
  return Make_Authorized_API_Call<OnboardingStep>(
    API_ENDPOINTS.ONBOARDING.STEP_UPDATE,
    'PUT',
    {
      Client_ID: clientId,
      ...data
    }
  );
}

/**
 * Reset onboarding progress (for testing/admin purposes)
 * POST /api/onboarding/reset
 */
export async function resetOnboardingProgress(
  clientId: string
): Promise<TimComboAPIResponse<OnboardingProgress>> {
  return Make_Authorized_API_Call<OnboardingProgress>(
    API_ENDPOINTS.ONBOARDING.RESET,
    'POST',
    { Client_ID: clientId }
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get the icon component name for a step based on its action type
 */
export function getStepIconName(step: OnboardingStep): string {
  // Map common step types to icon names
  const iconMap: Record<string, string> = {
    'connect-repo': 'HiCode',
    'upload-docs': 'HiDocumentAdd',
    'create-spec': 'HiDocumentText',
    'run-scan': 'HiSearch',
    'invite-team': 'HiUserGroup',
    'configure-settings': 'HiCog',
    'setup-billing': 'HiCreditCard',
    'review-assessment': 'HiClipboardCheck',
  };

  return step.icon || iconMap[step.step_id] || 'HiCheckCircle';
}

/**
 * Get progress color based on completion percentage
 */
export function getProgressColor(percentage: number): 'red' | 'yellow' | 'green' | 'blue' {
  if (percentage >= 100) return 'green';
  if (percentage >= 66) return 'blue';
  if (percentage >= 33) return 'yellow';
  return 'red';
}

/**
 * Check if client is considered "onboarding" (not fully onboarded)
 */
export function isOnboarding(progress: OnboardingProgress): boolean {
  return progress.completion_percentage < 100;
}

/**
 * Check if client has completed all required steps
 */
export function hasCompletedRequiredSteps(progress: OnboardingProgress): boolean {
  return progress.required_steps_completed >= progress.total_required_steps;
}

export default {
  getOnboardingProgress,
  updateOnboardingStep,
  resetOnboardingProgress,
  getStepIconName,
  getProgressColor,
  isOnboarding,
  hasCompletedRequiredSteps,
};
